/**
 * Deciding whether a workout is FINISHED, and repairing one whose own state
 * field says it is not.
 *
 * These functions are one subject and they all rest on one rule, which is worth
 * reading before touching any of them:
 *
 * **A set is written into the stored document only once the user has CONCLUDED
 * it.** So a user who did two sets of four and walked away leaves a document
 * whose every stored set says "done", exactly like a user who finished. Judged
 * from the stored document alone, a finished workout and an abandoned one cannot
 * be told apart. Only the workout page can tell them apart, because the page
 * holds a placeholder for every set the user never reached.
 *
 * That rule used to be carried by comments repeated on several functions here.
 * It is now carried by a type: `WorkoutSetStatesOnScreen` below, which only
 * `buildWorkoutSetStatesOnScreen` can produce, so a list derived from the stored
 * document no longer fits where the screen's list is required.
 *
 * The three functions that read the STORED document on purpose —
 * `exerciseHoldsConcludedSet`, `workoutHoldsConcludedSet` and
 * `resolveUnskippedWorkoutState` — keep their warnings in prose, because their
 * warning is a different one: not "do not pass me the wrong list" but "do not use
 * my ANSWER to decide whether a workout is finished". No type can say that.
 */

import { Log_Workout, Log_WorkoutExercise } from './log-workout';
import { LogRecordState } from './user-log';

/**
 * The marker that makes `WorkoutSetStatesOnScreen` a type only
 * `buildWorkoutSetStatesOnScreen` produces. It has no value at run time — it is
 * declared, never defined, and nothing ever reads it. It is exported only
 * because TypeScript cannot write this module's type declarations without a name
 * for it.
 */
export declare const BUILT_BY_THE_WORKOUT_SCREEN: unique symbol;

/** What one exercise looks like on screen: which exercise it is, and the state
 *  of every set the page is showing for it, in order. */
export interface ExerciseSetStatesOnScreen {
  subSlotId: string;
  setStates: readonly (LogRecordState | undefined)[];
}

/**
 * The set states of a whole workout AS THE SCREEN SHOWS THEM — one entry per
 * exercise, each holding that exercise's set states in order, including the sets
 * the user has not reached yet.
 *
 * Only `buildWorkoutSetStatesOnScreen` can make one. That is the whole point:
 * see the warning on that function.
 */
export interface WorkoutSetStatesOnScreen {
  readonly exercises: readonly ExerciseSetStatesOnScreen[];
  /** phantom marker — this property never exists at run time */
  readonly [BUILT_BY_THE_WORKOUT_SCREEN]: true;
}

/**
 * Build the screen's view of a workout's set states.
 *
 * # WHAT YOU MUST PASS, and why getting it wrong loses the user's work
 *
 * `exercises` must be built from what the WORKOUT PAGE is showing, which holds an
 * entry for every set the workout was meant to have — including the ones the user
 * has not reached yet, which arrive as `undefined`.
 *
 * Do NOT build it from the stored document's set list. A set is written into the
 * document only once the user has concluded it, so a user who did two sets of four
 * and walked away has a document whose every stored set is `Done`. Given that
 * list, `resolveConcludedWorkoutState` would close their workout as finished. The
 * unreached placeholders are the entire reason it is safe, so this is a real
 * contract and not a stylistic preference; there is a test named after it.
 *
 * This is the ONE place that warning is written, and the only door into the type
 * the functions below require. That is what it buys: every other route into them
 * is refused by the compiler rather than by a comment somebody has to read.
 */
export function buildWorkoutSetStatesOnScreen(
  exercises: readonly ExerciseSetStatesOnScreen[],
): WorkoutSetStatesOnScreen {
  // the marker is a type-level thing only, so the object really does just hold
  // the list — the cast is what stops anything else claiming to be this type
  return { exercises } as unknown as WorkoutSetStatesOnScreen;
}

/**
 * Is this one set finished with, from the user's point of view? That means
 * either they performed it (Done) or they deliberately passed over it
 * (Skipped). Anything else — still in progress, never opened, or a set the log
 * has no record of at all — is not finished with.
 *
 * `undefined` is a real answer here rather than a mistake. A stored workout can
 * hold gaps in its set list (see design-docs/sparse-set-healing.md, #720), and
 * the workout page builds screen-only placeholder sets for sets the user has
 * not reached yet. Both arrive here as `undefined` and both mean "not finished
 * with".
 */
export function isSetConcluded(state: LogRecordState | undefined): boolean {
  return state === LogRecordState.Done || state === LogRecordState.Skipped;
}

/**
 * Has the user finished with this whole exercise — every set either performed
 * or deliberately skipped?
 *
 * An exercise with NO sets answers false. That is deliberate and load-bearing:
 * "every set is finished with" is trivially true of an empty list, and the
 * callers below use this answer to decide whether a workout may be closed. A
 * workout carrying an exercise nobody has touched must never be closed on the
 * strength of an empty list.
 */
export function isExerciseConcluded(setStates: readonly (LogRecordState | undefined)[]): boolean {
  return setStates.length > 0 && setStates.every(isSetConcluded);
}

/**
 * The states a workout can be repaired FROM. An allow-list, deliberately, and
 * not "anything that is not finished": `LogRecordState` carries a fifth value,
 * `Planned`, which belongs to meals and should never appear on a workout — and
 * this whole family of bugs is about that field holding a value it should never
 * hold. A state nobody expected is left alone rather than repaired.
 */
const REPAIRABLE_WORKOUT_STATES: readonly LogRecordState[] = [
  LogRecordState.Incomplete,
  LogRecordState.Scheduled,
];

/**
 * Work out what a workout's own state should be, given the state of every set
 * in it. Returns the state to store.
 *
 * This only ever moves a workout FORWARD, to finished, and only when every
 * single set is finished with. It never moves a workout back, and it never
 * touches one that is already finished with (`Done` or `Skipped`).
 *
 * It exists because the workout's own state can be reverted between the moment
 * the app decides the workout is finished and the moment the workout is written
 * to storage — see #4802, and the comment at the call site in the workout page.
 *
 * What `setStatesOnScreen` has to be, and why it costs the user their work when
 * it is wrong, is on `buildWorkoutSetStatesOnScreen` — the only function that can
 * produce this argument.
 *
 * # Why a workout saying "never started" is repaired too (#4840)
 *
 * A workout can be stored as `Scheduled` while every set in it is `Done`:
 * undoing a skip used to write that, and so can a copy of the document arriving
 * from storage or the server. This used to return such a workout unchanged, so
 * a finished session showed as not started for ever, and — because the training
 * rotation will not advance past a past-dated `Scheduled` workout — the user was
 * offered the same day of their split again.
 *
 * That exclusion was originally deliberate: starting a workout suppresses that
 * day's workout reminder, so it seemed a workout should reach `Done` through
 * that transition rather than jumping past it. What settles it: the
 * suppression fires only on the `Scheduled` -> `Incomplete` step in
 * `concludeSet`, and a `Scheduled` -> `Done` transition that bypasses it already
 * existed: skipping every exercise of a never-started workout reaches
 * `onExerciseDone`, which writes `Done` directly. So nothing new is bypassed
 * here. And the protection that actually matters was never the state field: it
 * is the test below that every exercise holds at least one set and every set is
 * finished with, which a workout nobody has touched can never satisfy, because
 * its sets are all unreached placeholders.
 *
 * @param currentState the workout's state as it stands right now
 * @param setStatesOnScreen the set states of every exercise, as the page is showing them
 */
export function resolveConcludedWorkoutState(
  currentState: LogRecordState,
  setStatesOnScreen: WorkoutSetStatesOnScreen,
): LogRecordState {
  if (!REPAIRABLE_WORKOUT_STATES.includes(currentState)) return currentState;
  const exercises = setStatesOnScreen.exercises;
  // a workout holding no exercises at all is not a finished workout
  if (exercises.length === 0) return currentState;
  if (!exercises.every(exercise => isExerciseConcluded(exercise.setStates))) return currentState;
  return LogRecordState.Done;
}

/**
 * Does the screen show every exercise the stored workout holds?
 *
 * `resolveConcludedWorkoutState` above reads the set list the SCREEN built,
 * because only the screen holds the sets the user never reached. That is sound
 * only while the screen shows the whole workout, and it does not always: the
 * loader drops an exercise it cannot display — its entry in the program is gone
 * and the workout carries no snapshot of it, loading it threw, or the database
 * connection died part-way through the load.
 *
 * A dropped exercise is invisible to the repair: it cannot stop the workout
 * being judged finished, so a session the user really did abandon gets closed
 * (#4960).
 *
 * The dropped exercise no longer costs the user their logged sets, which it
 * used to — the save replaced the stored exercise list with the on-screen one.
 * `mergeScreenExercisesOverStored` in `log-workout.ts` is what changed that, and
 * the two are deliberately separate questions: that one keeps the user's data
 * whatever the screen is missing, while this one decides whether the screen may be
 * TRUSTED to say the workout is over. Answering the second with the first would
 * close a workout on the strength of a set list nobody has seen.
 *
 * Exercises are matched by sub-slot id, which is what identifies an exercise
 * within a workout. An exercise with no id counts as NOT covered: it cannot be
 * matched against anything, and of the two ways to be wrong, refusing a repair
 * costs a leftover card while allowing one closes a workout the user has not
 * finished.
 *
 * The two id lists are passed as one named object rather than as two arguments
 * in a fixed order. They are the same type, so nothing but the order told them
 * apart, and swapping them silently answers a different question.
 *
 * @param ids.storedSubSlotIds  the sub-slot id of every exercise the workout document holds
 * @param ids.onScreenSubSlotIds the sub-slot id of every exercise the page managed to load
 */
export function screenHoldsEveryStoredExercise(ids: {
  storedSubSlotIds: readonly (string | undefined)[];
  onScreenSubSlotIds: readonly (string | undefined)[];
}): boolean {
  const onScreen = new Set(ids.onScreenSubSlotIds.filter(id => !!id));
  return ids.storedSubSlotIds.every(id => !!id && onScreen.has(id));
}

/**
 * Does this exercise hold a set the user finished with — performed or
 * deliberately skipped?
 *
 * A stored set list can carry holes where a set should be, which is why
 * `healSparseLogSets` exists (#720, design-docs/sync-integrity.md). A hole
 * reaches `isSetConcluded` as `undefined`, which already answers "not finished
 * with". A damaged document can also hold something that is not a list at all;
 * that reads as holding no finished set rather than throwing, since the conflict
 * resolver asks this of raw stored copies (#6825).
 */
export function exerciseHoldsConcludedSet(exercise: Log_WorkoutExercise | undefined): boolean {
  return Array.isArray(exercise?.sets) && exercise.sets.some(s => isSetConcluded(s?.state));
}

/**
 * Does this stored workout hold any set the user has finished with — performed
 * or deliberately skipped?
 *
 * This is the one place that asks "is there anything of the user's in here?",
 * and both callers derive their answer from it rather than walking the document
 * themselves, so the two cannot drift apart. It reads the STORED document, so
 * see the warning on `buildWorkoutSetStatesOnScreen` before reaching for it to
 * decide whether a workout is FINISHED — that is a different question, and the
 * stored document cannot answer it.
 *
 * It has to survive the shapes real documents have. `exercises` is deprecated
 * and computed on demand, so an older document can be missing it altogether;
 * the per-exercise half is `exerciseHoldsConcludedSet` above, which is where
 * the sparse-set and not-a-list cases are explained.
 */
export function workoutHoldsConcludedSet(workout: Log_Workout): boolean {
  return Array.isArray(workout?.exercises) && workout.exercises.some(exerciseHoldsConcludedSet);
}

/**
 * What state should a workout go back to when the user undoes a skip?
 *
 * Undoing a skip used to write `Scheduled` — "never started" — whatever the
 * workout held, so a session the user had fully logged came back claiming they
 * had not begun it (#4840). Worse, nothing could then put it right: every
 * repair for #4802 asked for `Incomplete` specifically, and the training
 * rotation refuses to advance past a past-dated `Scheduled` workout, so the
 * user was offered the same day of their split again.
 *
 * # Why this never answers `Done`, even for a workout that is plainly finished
 *
 * A set is written into the document only once the user has concluded it (see
 * `concludeSet` on the workout page). So a user who did two sets of four and
 * walked away leaves a document whose every STORED set is `Done`, exactly like
 * one who finished. Judged from the document alone the two cannot be told
 * apart, and answering `Done` here would close sessions that are genuinely
 * unfinished — a worse bug than the one this fixes.
 *
 * Only the workout page knows how many sets there were meant to be, because the
 * loader fills in the ones the user never reached. So this answers `Incomplete`
 * and the page promotes it to `Done` the next time the workout is opened, via
 * `resolveConcludedWorkoutState`. The cost is that a finished workout which is
 * skipped and restored offers "Resume" until it is next opened, which repairs
 * itself and puts no data at risk.
 */
export function resolveUnskippedWorkoutState(workout: Log_Workout): LogRecordState {
  return workoutHoldsConcludedSet(workout) ? LogRecordState.Incomplete : LogRecordState.Scheduled;
}

/** One set the screen has finished with that the stored workout does not have. */
export interface MissingConcludedSet {
  /** which exercise of the workout, by its plan sub-slot id */
  subSlotId: string;
  /** the set's position in that exercise, counting from zero */
  setIndex: number;
}

/**
 * Find every set the user has finished with on screen that the stored workout
 * does not have (#4832).
 *
 * # Why this is needed
 *
 * The workout page holds the one shared object the whole app uses for that
 * document, and a stored body read from disk can replace its exercise list —
 * taking with it any set concluded since that body was written. The page repairs
 * that before every save; this function is what REPORTS it, so we find out it
 * happened instead of hearing about it from a customer whose set disappeared.
 * `design-docs/sync-integrity.md` has the mechanism.
 *
 * # The rule that keeps this quiet in the ordinary case
 *
 * Only a set the user has FINISHED WITH counts — performed, or deliberately
 * skipped. A set is written into the document only once it is concluded, so the
 * screen legitimately holds a placeholder for every set the user has not reached
 * yet, and the document has never seen any of them. Counting those would make
 * this fire on every workout in progress, and a report that fires constantly is
 * a report nobody reads.
 *
 * A stored set that exists but says the user has not finished with it counts the
 * same as one that is absent: either way the document disagrees with what the
 * user has done. That covers a gap in a stored set list too (#720,
 * design-docs/sparse-set-healing.md), which arrives here as an absent set.
 *
 * @param onScreen the set states of every exercise the page is showing, in screen order
 * @param stored the exercises as the workout document currently holds them
 * @returns one entry per missing set, in screen order then set order; empty when
 *          the document holds everything the screen has finished with
 */
export function findConcludedSetsMissingFromDocument(
  onScreen: WorkoutSetStatesOnScreen,
  stored: readonly Log_WorkoutExercise[],
): MissingConcludedSet[] {
  const storedBySubSlotId = new Map<string, Log_WorkoutExercise>();
  for (const exercise of stored) {
    storedBySubSlotId.set(exercise.subSlotId, exercise);
  }

  const missing: MissingConcludedSet[] = [];
  // for each exercise on screen, in the order the user sees it
  for (const exercise of onScreen.exercises) {
    const storedSets = storedBySubSlotId.get(exercise.subSlotId)?.sets;
    // for each set the user has finished with, check the document has it too
    for (let setIndex = 0; setIndex < exercise.setStates.length; setIndex++) {
      if (!isSetConcluded(exercise.setStates[setIndex])) continue;
      if (isSetConcluded(storedSets?.[setIndex]?.state)) continue;
      missing.push({ subSlotId: exercise.subSlotId, setIndex });
    }
  }
  return missing;
}
